import React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useActivationUsage,
  useCompanySubscription,
  useUpcomingInvoice,
} from 'hooks/api'
import { useReferrerState } from 'hooks'
import type { ActivationUsageRange } from 'services/billingApi'
import { ROUTES } from 'routes'

import { BillingOverview } from 'components'

/**
 * Company-settings Billing overview — thin wrapper around the shared
 * <BillingOverview> (also used by the internal company-detail Plan tab, VERA-873),
 * wiring the customer self-service subscription/invoice data + settings navigation.
 */
export const Overview = () => {
  const navigate = useNavigate()
  // Carries "← Back to Billing" to both cross-section destinations below.
  const referrer = useReferrerState('Billing')

  const { data: subscription, isLoading: subLoading } = useCompanySubscription(
    null,
    true,
  )
  const invoiceEnabled = Boolean(subscription?.stripe_subscription_id)
  const { data: upcomingInvoice, isLoading: invoiceLoading } =
    useUpcomingInvoice(invoiceEnabled)
  // The chart's range lives HERE, not in <BillingOverview>, because changing it
  // has to re-run the query — the endpoint buckets server-side rather than
  // handing back a year of rows to slice. The card renders the control and calls
  // back; it does not own the fetch.
  const [activationRange, setActivationRange] =
    React.useState<ActivationUsageRange>('month')
  const {
    data: activationUsage,
    isLoading: activationUsageLoading,
    // `isFetching` covers the background refetch a range switch triggers, where
    // `isLoading` is already false because the previous response is still on
    // screen. The card needs both: the first skeletons a tile, the second tells
    // the chart that a mismatched response is in flight rather than broken.
    isFetching: activationUsageFetching,
    refetch: refetchActivationUsage,
  } = useActivationUsage(activationRange)

  // Skeleton the Next-invoice-summary cells while the subscription (and then the
  // upcoming invoice, once enabled) are still resolving.
  const upcomingInvoiceLoading = subLoading || (invoiceEnabled && invoiceLoading)

  return (
    <BillingOverview
      subscription={subscription}
      upcomingInvoice={upcomingInvoice}
      upcomingInvoiceLoading={upcomingInvoiceLoading}
      activationUsage={activationUsage}
      activationUsageLoading={activationUsageLoading}
      activationRange={activationRange}
      onActivationRangeChange={setActivationRange}
      activationUsageFetching={activationUsageFetching}
      onRetryActivationUsage={() => {
        void refetchActivationUsage()
      }}
      companyId={subscription?.company_id}
      onChangePlan={() =>
        navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN, { state: referrer })
      }
      onChangeActivations={() =>
        navigate(ROUTES.SETTINGS_COMPANY_FEATURES, { state: referrer })
      }
    />
  )
}
